import { useState } from 'react';
import { Info, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';

interface ContractionInfoBoxProps {
  initiallyExpanded?: boolean;
}

export function ContractionInfoBox({ initiallyExpanded = true }: ContractionInfoBoxProps) {
  const { t } = useTranslation('contractionTimer');
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  return (
    <div className="glass-effect rounded-[15px] overflow-hidden">
      {/* Header - clickable to toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-pp-orange" />
          <span className="font-medium text-white">
            {t('info.title')}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-white/50" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/50" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Practice Contractions */}
          <div>
            <h4 className="font-medium text-white mb-2">
              {t('info.practiceTitle')}
            </h4>
            <ul className="space-y-1.5">
              <InfoItem
                icon={<X className="w-3 h-3" />}
                iconColor="text-red-400"
                text={t('info.practiceItem1')}
              />
              <InfoItem
                icon={<X className="w-3 h-3" />}
                iconColor="text-red-400"
                text={t('info.practiceItem2')}
              />
              <InfoItem
                icon={<Check className="w-3 h-3" />}
                iconColor="text-white"
                text={t('info.practiceItem3')}
              />
              <InfoItem
                icon={<X className="w-3 h-3" />}
                iconColor="text-red-400"
                text={t('info.practiceItem4')}
              />
              <InfoItem
                icon={<Check className="w-3 h-3" />}
                iconColor="text-white"
                text={t('info.practiceItem5')}
              />
            </ul>
          </div>

          {/* Real Contractions */}
          <div>
            <h4 className="font-medium text-white mb-2">
              {t('info.realTitle')}
            </h4>
            <ul className="space-y-1.5">
              <InfoItem
                icon={<Check className="w-3 h-3" />}
                iconColor="text-white"
                text={t('info.realItem1')}
              />
              <InfoItem
                icon={<Check className="w-3 h-3" />}
                iconColor="text-white"
                text={t('info.realItem2')}
              />
              <InfoItem
                icon={<X className="w-3 h-3" />}
                iconColor="text-red-400"
                text={t('info.realItem3')}
              />
              <InfoItem
                icon={<Check className="w-3 h-3" />}
                iconColor="text-white"
                text={t('info.realItem4')}
              />
              <InfoItem
                icon={<Check className="w-3 h-3" />}
                iconColor="text-white"
                text={t('info.realItem5')}
              />
            </ul>
          </div>

          {/* Tip Box */}
          <div className="p-3 bg-white/10 rounded-xl">
            <p className="text-sm text-white/80">
              <span className="font-medium">{t('info.tipLabel')}</span> {t('info.tipText')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface InfoItemProps {
  icon: React.ReactNode;
  iconColor: string;
  text: string;
}

function InfoItem({ icon, iconColor, text }: InfoItemProps) {
  return (
    <li className="flex items-start gap-2 text-sm text-white/80">
      <span
        className={cn(
          'mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center',
          iconColor
        )}
      >
        {icon}
      </span>
      <span>{text}</span>
    </li>
  );
}
